import { LeftIcon } from 'foundations-assets/icons/components/LeftIcon';
import { RightIcon } from 'foundations-assets/icons/components/RightIcon';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Children, useCallback, useEffect, useMemo, useState } from 'react';
import I18n from 'I18n';
import UsageTracker from '../utils/UsageTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const NEXT_PAGE_TIMEOUT = 20000;
export const SectionCarousel = ({
  children
}) => {
  const [pageIndex, setPageIndex] = useState(0);
  const [visitedPages, setVisitedPages] = useState([0]);
  const [touched, setTouched] = useState(false);
  const [hovering, setHovering] = useState(false);

  // Children.toArray strips null/undefined/boolean, so pageCount
  // only reflects children that actually render content.
  const validChildren = useMemo(() => Children.toArray(children), [children]);
  const pageCount = validChildren.length;
  const sections = useMemo(() => validChildren.map((section, index) => {
    if (!visitedPages.includes(index)) return null;
    return /*#__PURE__*/_jsx(View, {
      display: index === pageIndex ? 'block' : 'none',
      children: section
    }, `section-wrapper-${index}`);
  }), [validChildren, pageIndex, visitedPages]);
  const trackPageChange = useCallback((fromIndex, toIndex, trigger) => {
    UsageTracker.trackStandalone('Learning Center Interaction', {
      action: 'homepage-carousel-page-change',
      screen: 'homepage',
      section: 'dynamic-content-carousel',
      from: String(fromIndex + 1),
      position: String(toIndex + 1),
      whichButton: trigger
    });
  }, []);
  const handlePrevious = useCallback(() => {
    const nextIndex = pageIndex > 0 ? pageIndex - 1 : pageCount - 1;
    setPageIndex(nextIndex);
    setVisitedPages(pages => pages.includes(nextIndex) ? pages : [...pages, nextIndex]);
    trackPageChange(pageIndex, nextIndex, 'manual');
  }, [pageIndex, pageCount, trackPageChange]);
  const handleNext = useCallback((trigger = 'manual') => {
    const nextIndex = (pageIndex + 1) % pageCount;
    setPageIndex(nextIndex);
    setVisitedPages(pages => pages.includes(nextIndex) ? pages : [...pages, nextIndex]);
    trackPageChange(pageIndex, nextIndex, trigger);
  }, [pageIndex, pageCount, trackPageChange]);
  useEffect(() => {
    if (!touched && !hovering) {
      const scrollInterval = setInterval(() => {
        handleNext('automatic');
      }, NEXT_PAGE_TIMEOUT);
      return () => {
        clearInterval(scrollInterval);
      };
    }
  }, [handleNext, hovering, touched]);
  return /*#__PURE__*/_jsxs(View, {
    onMouseEnter: () => setHovering(true),
    onMouseLeave: () => setHovering(false),
    children: [/*#__PURE__*/_jsx(View, {
      marginBlockEnd: "400",
      position: "relative",
      children: sections
    }), /*#__PURE__*/_jsx(TFlex, {
      justifyContent: "center",
      children: /*#__PURE__*/_jsx(View, {
        backgroundColor: "fill-surface-default",
        borderRadius: "400",
        inlineSize: "fit-content",
        paddingBlock: "100",
        paddingInline: "400",
        children: /*#__PURE__*/_jsxs(TFlex, {
          alignItems: "center",
          children: [/*#__PURE__*/_jsx(IconButton, {
            "aria-label": I18n.text('learningCenter.common.loadPreviousButton'),
            margin: "0",
            onClick: () => {
              handlePrevious();
              setTouched(true);
            },
            use: "unstyled",
            children: /*#__PURE__*/_jsx(LeftIcon, {})
          }), /*#__PURE__*/_jsxs(Text, {
            display: "inline-block",
            marginInline: "200",
            children: [pageIndex + 1, " / ", pageCount]
          }), /*#__PURE__*/_jsx(IconButton, {
            "aria-label": I18n.text('learningCenter.common.loadNextButton'),
            margin: "0",
            onClick: () => {
              handleNext();
              setTouched(true);
            },
            use: "unstyled",
            children: /*#__PURE__*/_jsx(RightIcon, {})
          })]
        })
      })
    })]
  });
};